import { ObjectHelper } from '../helpers';
import { ColumnGroup, ColumnOptions } from '../models';

type Constructor<T = {}> = new (...args: any[]) => T;

export function GridRow<TBase extends Constructor>(Base: TBase) {
  return class extends Base {
    dirty: Map<string, boolean> = new Map<string, boolean>();
    originalValues: Map<string, any> = new Map<string, any>();
    errors?: Map<string, string> = new Map<string, string>();
    warnings?: Map<string, string> = new Map<string, string>();
    locked: boolean = false;

    constructor(...args: any[]) {
      super(...args);

      this.setOriginalValues(this);
    }

    setOriginalValues(instance: any): void {
      const columns = this.getColumns();
      columns.forEach((column) => {
        const value = Reflect.get(instance, column.field);
        if (value !== undefined) {
          instance.originalValues.set(
            column.field,
            ObjectHelper.deepCopy(value),
          );
        }
      });
    }

    getColumnGroups(columns: ColumnOptions[]): ColumnGroup[] {
      const groups: ColumnGroup[] = [];
      columns.forEach((column) => {
        if (column.group) {
          let group = groups.find((g) => g.title === column.group);
          if (!group) {
            group = {
              title: column.group,
              columns: [],
              headerClass: column.groupHeaderClass,
            };
            groups.push(group);
          }
          group.columns.push(column);
        }
      });
      return groups;
    }

    getColumns(): ColumnOptions[] {
      const properties = Object.getOwnPropertyNames(this);
      let proto = Object.getPrototypeOf(this);
      while (proto && proto !== Object.prototype) {
        const protoDescriptors = Object.getOwnPropertyDescriptors(proto);
        for (const [key, descriptor] of Object.entries(protoDescriptors)) {
          if (typeof descriptor.get === 'function') {
            properties.push(key);
          }
        }
        proto = Object.getPrototypeOf(proto);
      }

      const columns = properties
        .map((property) => {
          return Reflect.getMetadata('columnOptions', this, property);
        })
        .filter((column) => column !== undefined);

      return ObjectHelper.deepCopy(columns.sort((a, b) => a.order - b.order));
    }

    hasDirty(): boolean {
      return Array.from(this.dirty.values()).some((dirty) => dirty);
    }
  };
}
